import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  Card,
  CardTitle,
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { PptxObjectType } from '@/utils/export-pptx/constants'
import { KolPerformanceSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: KolPerformanceSlide
  onSlideReady?: (slide: KolPerformanceSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape}>
        {slide.payload.platform && (
          <PlatformImage
            height={54}
            platform={slide.payload.platform}
            width={54}
          />
        )}

        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'insight_report:slide_kol_performance_title' })}
        </span>
      </SlideTitle>

      <Card data-pptx-object={PptxObjectType.Shape}>
        <CardTitle data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'insight_report:performance_data' })}
        </CardTitle>

        <GridContent>
          {slide.payload.includes.stats && (
            <ContentCard data-pptx-object={PptxObjectType.Shape} span={2}>
              <ContentCardItem>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'general:column_title_post_count' })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.postCount}
                </ContentCardItemValue>
              </ContentCardItem>

              <ContentCardItem>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'general:column_title_like_count' })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.likeCount}
                </ContentCardItemValue>
              </ContentCardItem>

              <ContentCardItem>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'general:column_title_comment_count' })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.commentCount}
                </ContentCardItemValue>
              </ContentCardItem>

              <ContentCardItem>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'general:column_title_share_count' })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.shareCount}
                </ContentCardItemValue>
              </ContentCardItem>
            </ContentCard>
          )}

          {slide.payload.includes.engagement && (
            <ContentCard data-pptx-object={PptxObjectType.Shape}>
              <ContentCardTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'insight_report:detail_performance_data_engagement',
                })}
              </ContentCardTitle>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_engagement_count',
                  })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.engagementCount}
                </ContentCardItemValue>
              </ContentCardItem>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_engagement_rate',
                  })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.engagementRate}
                </ContentCardItemValue>
              </ContentCardItem>
            </ContentCard>
          )}

          {slide.payload.includes.view && (
            <ContentCard data-pptx-object={PptxObjectType.Shape}>
              <ContentCardTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'insight_report:detail_performance_data_view',
                })}
              </ContentCardTitle>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_watch_count',
                  })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.viewCount}
                </ContentCardItemValue>
              </ContentCardItem>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_view_rate',
                  })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.viewRate}
                </ContentCardItemValue>
              </ContentCardItem>
            </ContentCard>
          )}

          {slide.payload.includes.reach && (
            <ContentCard data-pptx-object={PptxObjectType.Shape}>
              <ContentCardTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'insight_report:detail_performance_data_reach',
                })}
              </ContentCardTitle>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'general:column_title_touch_count' })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.reachCount}
                </ContentCardItemValue>
              </ContentCardItem>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'general:column_title_touch_rate' })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.reachRate}
                </ContentCardItemValue>
              </ContentCardItem>
            </ContentCard>
          )}

          {slide.payload.includes.click && (
            <ContentCard data-pptx-object={PptxObjectType.Shape}>
              <ContentCardTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'insight_report:detail_performance_data_click',
                })}
              </ContentCardTitle>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_link_click_count',
                  })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.clickCount}
                </ContentCardItemValue>
              </ContentCardItem>

              <ContentCardItem span={2}>
                <ContentCardItemTitle data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({
                    id: 'general:column_title_link_click_rate',
                  })}
                </ContentCardItemTitle>
                <ContentCardItemValue data-pptx-object={PptxObjectType.Text}>
                  {slide.payload.clickRate}
                </ContentCardItemValue>
              </ContentCardItem>
            </ContentCard>
          )}
        </GridContent>
      </Card>
    </SlideContainer>
  )
}

const GridContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  margin-bottom: 16px;
`

const ContentCard = styled.div<{ span?: number }>`
  grid-column: span ${({ span }): number => span ?? 1};

  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  padding: 12px 16px;

  background-color: ${({ theme }): string => theme.colors.background.grey};
  border-radius: 6px;
`

const ContentCardTitle = styled.p`
  grid-column: span 4;
  margin-bottom: 0;

  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const ContentCardItem = styled.div<{ span?: number }>`
  grid-column: span ${({ span }): number => span ?? 1};

  flex-grow: 1;
`

const ContentCardItemTitle = styled.p`
  margin-bottom: 0px;

  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const ContentCardItemValue = styled.p`
  margin-bottom: 0px;

  font-size: 24px;
  font-weight: 700;
  line-height: 140%;

  color: ${({ theme }): string => theme.colors.text.primary};
`

export default SlideComponent
